import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Tag, message } from "antd";
import { ReloadOutlined, ClusterOutlined } from "@ant-design/icons";
import { getRabbitQueuesApi, getRabbitOverviewApi } from "../../services/api";
import StatCard from "./StatCard";

const STATE_COLORS = {
  running: "#43A047",
  idle: "#FFA726",
  flow: "#1E88E5",
  down: "#E24B4A",
};

export default function QueueMonitor() {
  const [queues, setQueues] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [errored, setErrored] = useState(false);

  const fetchAll = useCallback(async (showErrors) => {
    setLoading(true);
    try {
      const [qRes, oRes] = await Promise.all([getRabbitQueuesApi(), getRabbitOverviewApi()]);

      if (qRes.data?.status) {
        setQueues(qRes.data.data || []);
        setErrored(false);
      } else {
        setErrored(true);
        if (showErrors) message.error(qRes.data?.data || "Failed to load queues");
      }

      if (oRes.data?.status) setOverview(oRes.data.data || null);

      setLastUpdated(new Date());
    } catch (err) {
      setErrored(true);
      if (showErrors) message.error("Failed to reach the backend");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(true);
  }, [fetchAll]);

  // Live view — same 8s cadence as other near-real-time panels in admin.
  useEffect(() => {
    const interval = setInterval(() => fetchAll(false), 8000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const totalPending = queues.reduce((sum, q) => sum + (q.messagesReady || 0), 0);
  const totalUnacked = queues.reduce((sum, q) => sum + (q.messagesUnacknowledged || 0), 0);

  const columns = [
    {
      title: "Queue",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (v) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{v}</span>,
    },
    {
      title: "State",
      dataIndex: "state",
      width: 100,
      render: (v) => (
        <Tag color={STATE_COLORS[v] || "#888"} style={{ borderRadius: 12, textTransform: "capitalize" }}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Pending",
      dataIndex: "messagesReady",
      width: 110,
      sorter: (a, b) => (a.messagesReady || 0) - (b.messagesReady || 0),
      defaultSortOrder: "descend",
      render: (v) => (
        <span style={{ fontWeight: 700, color: v > 0 ? "#e24b4a" : "#888" }}>{v ?? 0}</span>
      ),
    },
    {
      title: "Unacked",
      dataIndex: "messagesUnacknowledged",
      width: 100,
      render: (v) => <span style={{ color: v > 0 ? "#d9770a" : "#888" }}>{v ?? 0}</span>,
    },
    {
      title: "Total",
      dataIndex: "messagesTotal",
      width: 90,
    },
    {
      title: "Consumers",
      dataIndex: "consumers",
      width: 110,
      render: (v) => (
        <span style={{ color: v > 0 ? "#1d9e75" : "#e24b4a", fontWeight: 600 }}>{v ?? 0}</span>
      ),
    },
    {
      title: "Publish/s",
      dataIndex: "publishRate",
      width: 100,
      render: (v) => (v ? v.toFixed(2) : "0.00"),
    },
    {
      title: "Deliver/s",
      dataIndex: "deliverRate",
      width: 100,
      render: (v) => (v ? v.toFixed(2) : "0.00"),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">
            <ClusterOutlined style={{ marginRight: 8, color: "#534ab7" }} />
            Message Queues
          </div>
          <div className="page-sub">
            Live RabbitMQ queue status — auto-refreshes every 8s
            {lastUpdated && ` · last updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchAll(true)} loading={loading}>
          Refresh
        </Button>
      </div>

      {errored && (
        <div style={{
          background: "#fcebeb", color: "#a32d2d", borderRadius: 10, padding: "12px 16px",
          marginBottom: 16, fontSize: 13,
        }}>
          Couldn't reach the RabbitMQ management API. The broker or its management plugin may be
          unreachable, or the credentials in kidsz's config may be out of date.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Queues" value={overview?.totalQueues ?? queues.length} />
        <StatCard label="Pending Messages" value={totalPending} color={totalPending > 0 ? "#e24b4a" : "#111"} />
        <StatCard label="Unacknowledged" value={totalUnacked} color={totalUnacked > 0 ? "#d9770a" : "#111"} />
        <StatCard label="Active Consumers" value={overview?.totalConsumers ?? "-"} />
        <StatCard label="Connections" value={overview?.totalConnections ?? "-"} />
      </div>

      <Table
        rowKey="name"
        columns={columns}
        dataSource={queues}
        loading={loading && queues.length === 0}
        pagination={false}
        size="middle"
      />
    </div>
  );
}
